/**
 * Stale-chunk recovery for code-split (lazy `import()`) modules.
 *
 * Quilty ships a code-split SvelteKit/Vite bundle: most analysis tabs and many
 * panels are loaded on demand via dynamic `import()`. After a deploy the hashed
 * chunk filenames change, so any browser tab that was opened *before* the
 * deploy still references the old filenames. The first lazy import then 404s
 * ("Failed to fetch dynamically imported module"), which surfaces to users as
 * "Failed to load tab. Please try again." even though the analysis data is
 * perfectly fine.
 *
 * The fix is two-layered (both wired through this module):
 *   1. Transient retry — re-attempt the import a couple of times in case the
 *      404/network blip is momentary.
 *   2. One-time reload — if it still fails with a chunk-load error, the running
 *      app is stale; a single full reload pulls the fresh asset manifest. A
 *      sessionStorage cooldown prevents reload loops when a chunk is genuinely
 *      gone (a truly broken deploy).
 */
import { logger } from '$lib/utils/logger';

const RELOAD_COOLDOWN_KEY = 'quilty:chunkReloadAt';
const RELOAD_COOLDOWN_MS = 30_000;

/**
 * Heuristically detect the family of errors browsers throw when a dynamically
 * imported module (or its preloaded CSS) fails to load. Covers Chrome,
 * Firefox, and Safari wording plus the explicit 404 case.
 */
export function isChunkLoadError(err: unknown): boolean {
  const msg = (err instanceof Error ? err.message : String(err ?? '')).toLowerCase();
  if (!msg) return false;
  return (
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('unable to preload css') ||
    msg.includes('failed to import') ||
    (msg.includes('module') && msg.includes('404')) ||
    (msg.includes('module script') && msg.includes('failed'))
  );
}

/**
 * Reload the page at most once per cooldown window to pick up a fresh asset
 * manifest. Returns true when a reload was actually triggered so callers can
 * decide whether to suppress a now-irrelevant error.
 */
export function reloadForStaleChunkOnce(reason: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const now = Date.now();
    const last = Number(window.sessionStorage.getItem(RELOAD_COOLDOWN_KEY) || '0');
    if (Number.isFinite(last) && now - last < RELOAD_COOLDOWN_MS) {
      // Already reloaded recently — the chunk is likely truly missing, so let
      // the caller surface a normal error instead of looping.
      return false;
    }
    window.sessionStorage.setItem(RELOAD_COOLDOWN_KEY, String(now));
    logger.warn('Reloading to recover from a stale code-split chunk', { reason });
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

interface ImportRetryOptions {
  /** Number of additional attempts after the first (default 2). */
  retries?: number;
  /** Base delay between attempts in ms; grows linearly per attempt (default 350). */
  delayMs?: number;
  /** Trigger a guarded full reload after retries are exhausted (default true). */
  reloadOnFailure?: boolean;
  /** Label used in logs/reload reason. */
  label?: string;
}

const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Run a lazy `import()` with retry + stale-chunk recovery. Non-chunk errors
 * (e.g. a real runtime error inside the module) are rethrown immediately so we
 * never mask genuine bugs behind a reload.
 */
export async function importWithRetry<T>(
  loader: () => Promise<T>,
  options: ImportRetryOptions = {},
): Promise<T> {
  const retries = options.retries ?? 2;
  const delayMs = options.delayMs ?? 350;
  const label = options.label ?? 'module';
  let lastErr: unknown;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await loader();
    } catch (err) {
      lastErr = err;
      if (!isChunkLoadError(err)) {
        throw err;
      }
      if (attempt < retries) {
        logger.warn('Lazy import failed, retrying', { label, attempt: attempt + 1 });
        await sleep(delayMs * (attempt + 1));
      }
    }
  }

  if (options.reloadOnFailure !== false) {
    reloadForStaleChunkOnce(`import:${label}`);
  }
  throw lastErr;
}
